<template>
    <section
        class="slice slice-testimonials slice-testimonials--single"
        :class="{'slice-testimonials--single--grey': backgroundColor === 'grey'}"
        aria-label="testimonials"
        data-testid="testimonials-single-slice"
    >
        <div class="slice-content">
            <MktTitleBrand :field="slice.primary.title" class="slice-testimonials--single__title" data-testid="testimonials-single-title" />

            <div
                :class="['slice-testimonials--single__quote', {'slice-testimonials--single__quote--white': slice.primary.backgroundColor === 'grey'}]"
            >
                <svg width="66" height="53" viewBox="0 0 66 53" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                        d="M31.0256 0.902219L28.7692 8.04752C17.4872 4.85093 7.7094 14.6287 7.89744 28.1672C18.4274 23.2783 27.641 30.2356 27.641 39.2612C27.641 46.9706 22 52.4236 14.6667 52.4236C6.01709 52.4236 0 45.2783 0 30.9877C0 9.17572 13.1624 -3.6106 31.0256 0.902219ZM66 0.902219L63.9316 8.04752C52.4615 4.85093 42.8718 14.6287 42.8718 28.1672C53.5897 23.2783 62.6154 30.2356 62.6154 39.2612C62.6154 46.9706 57.1624 52.4236 49.641 52.4236C41.1795 52.4236 35.1624 45.2783 35.1624 30.9877C35.1624 9.17572 48.1368 -3.6106 66 0.902219Z"
                        fill="#FF5C57"
                    />
                </svg>
                <PrismicRichText :field="slice.primary.quote" data-testid="testimonials-single-quote" />
                <div class="slice-testimonials--single__author-wrapper">
                    <div class="slice-testimonials--single__author">
                        <div data-testid="testimonials-single-author">{{ slice.primary.author }}</div>
                        <div data-testid="testimonials-single-author-job">{{ slice.primary.authorJob }}</div>
                    </div>
                    <div class="slice-testimonials--single__image-wrapper">
                        <MktCardHandAdaptive v-if="slice.primary.image" brand-theme>
                            <MktMaltPrismicImage :image-field="slice.primary.image" data-testid="testimonials-single-image" />
                        </MktCardHandAdaptive>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSlice} from '@prismicio-types';
    import {MktCardHandAdaptive, MktMaltPrismicImage, MktTitleBrand} from '#components';

    type TestimonialsSingleInZone = Extract<TestimonialsSlice, {variation: 'testimonialsSingle'}>;

    const props = defineProps(getSliceComponentProps<TestimonialsSingleInZone>());

    const backgroundColor = computed(() => props.slice.primary.backgroundColor);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-testimonials--single {
        &--grey {
            background-color: var(--joy-color-neutral-20);
        }

        .slice-content {
            @include mq.screen_md(min) {
                padding-left: 120px;
                padding-right: 120px;
            }
        }

        &__title {
            margin-bottom: var(--joy-core-spacing-9);

            @include mq.screen_md(min) {
                text-align: center;
                margin-bottom: var(--joy-core-spacing-6);
            }
        }

        &__quote {
            background-color: var(--joy-color-neutral-20);
            padding: var(--joy-core-spacing-9) var(--joy-core-spacing-4);
            border-radius: var(--joy-core-radius-4);
            text-align: left;

            &--white {
                background-color: var(--joy-color-background-default);
            }

            p {
                font-size: var(--joy-font-size-primary-600);
                font-family: var(--joy-font-family-title);
                line-height: 30px;
            }

            svg {
                margin-bottom: var(--joy-core-spacing-6);
                align-self: flex-start;
                flex: none;
            }

            @include mq.screen_md(min) {
                display: flex;
                align-items: center;
                padding: var(--joy-core-spacing-9) var(--joy-core-spacing-8);
                gap: var(--joy-core-spacing-9);
            }
        }

        &__image-wrapper {
            width: 80px;

            @include mq.screen_md(min) {
                width: 145px;
                margin-left: auto;
            }
        }

        &__author-wrapper {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-top: var(--joy-core-spacing-6);
            gap: var(--joy-core-spacing-4);

            @include mq.screen_md(min) {
                flex-direction: column-reverse;
                text-align: left;
                margin-top: initial;
            }
        }

        &__author {
            text-align: right;
            line-height: var(--joy-line-height-large);

            :first-child {
                font-size: var(--joy-font-size-primary-400);
                font-family: var(--joy-font-family-title);
            }

            :last-child {
                width: 200px;
                font-size: var(--joy-font-size-primary-300);
            }
        }
    }
</style>
